import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	Box,
	Typography,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { PersonAdd, SwapHoriz } from "@mui/icons-material";

import { notify } from "../../reducers/NotifierReducer";
import { formatDate, getEmployeeOptionLabel } from "../../utils/functions";
import {
	getEmployees,
	createVehicleAssignmentHistory,
	updateVehicleAssignmentHistory,
} from "../../utils/commonServices";
import { updateVehicleData } from "./useVehiclesData";
import { decrypt } from "../../utils/encryptionDecryptionHelper";

import DropdownController from "../../components/inputs/DropdownController";
import TextFieldController from "../../components/inputs/TextFieldController";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import PATCloseButton from "../../components/Buttons/PATCloseButton";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

// ─── Change Assignment Dialog
const changeAssignmentSchema = yup.object().shape({
	perPersonnelId: yup.string().required("Please select an employee"),
	notes: yup
		.string()
		.max(500, "Notes cannot exceed 500 characters")
		.nullable(),
});

const ChangeAssignmentDialog = ({
	open,
	onClose,
	onSave,
	vehicle,
	employees = [],
	currentAssignment,
	loading = false,
}) => {
	const {
		control,
		handleSubmit,
		reset,
		watch,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(changeAssignmentSchema),
		defaultValues: {
			perPersonnelId: "",
			notes: "",
		},
		mode: "onChange",
	});

	// Reset form when dialog opens
	useEffect(() => {
		if (open) {
			reset({
				perPersonnelId: "",
				notes: "",
			});
		}
	}, [open, reset]);

	const handleClose = () => {
		reset();
		onClose();
	};

	const onSubmit = (data) => {
		onSave({
			perPersonnelId: parseInt(data.perPersonnelId),
			notes: data.notes?.trim() || null,
		});
	};

	const perPersonnelId = watch("perPersonnelId");

	// Filter out current assignee from options
	const availableEmployees = useMemo(() => {
		return employees
			.filter((e) => e.isActive !== false)
			.filter((e) => {
				const empId =
					typeof e.value === "string" ? decrypt(e.value) : e.value;
				return empId !== vehicle?.perPersonnelId;
			});
	}, [employees, vehicle?.perPersonnelId]);

	// Get current assignee info
	const currentEmployee = useMemo(() => {
		if (!vehicle?.perPersonnelId) return null;
		return employees.find((e) => {
			const empId =
				typeof e.value === "string" ? decrypt(e.value) : e.value;
			return empId === vehicle.perPersonnelId;
		});
	}, [employees, vehicle?.perPersonnelId]);

	// Get selected employee info
	const selectedEmployee = useMemo(() => {
		if (!perPersonnelId) return null;
		return employees.find((e) => {
			const empId =
				typeof e.value === "string" ? decrypt(e.value) : e.value;
			return String(empId) === String(perPersonnelId);
		});
	}, [employees, perPersonnelId]);

	if (!vehicle) return null;

	const isReassignment = !!vehicle.perPersonnelId;
	const dialogTitle = isReassignment
		? `Change Assignment — ${vehicle.unitNumber}`
		: `Assign Vehicle — ${vehicle.unitNumber}`;

	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
			<DialogTitle>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					{isReassignment ? (
						<SwapHoriz color="primary" />
					) : (
						<PersonAdd color="primary" />
					)}
					{dialogTitle}
				</Box>
			</DialogTitle>

			<DialogContent>
				<Box
					component="form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 2,
						pt: 1,
					}}
				>
					{/* Vehicle Summary */}
					<Box
						sx={{
							display: "flex",
							gap: 1.5,
							p: 1.5,
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 1,
							alignItems: "center",
							bgcolor: "background.default",
						}}
					>
						<Typography sx={{ fontSize: 22 }}>🚗</Typography>
						<Box>
							<Typography variant="body2" fontWeight={800}>
								{vehicle.unitNumber} — {vehicle.year}{" "}
								{vehicle.make} {vehicle.model}
							</Typography>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									mt: 0.25,
								}}
							>
								<Typography
									variant="caption"
									color="text.secondary"
								>
									{vehicle.division || "—"}
								</Typography>
								{vehicle.status && (
									<Chip
										label={vehicle.status}
										size="small"
										color={
											vehicle.status === "In Service"
												? "success"
												: "error"
										}
										sx={{ height: 18, fontSize: 10 }}
									/>
								)}
							</Box>
						</Box>
					</Box>

					{/* Current Assignment Info */}
					<Box
						sx={{
							p: 1.5,
							bgcolor: currentEmployee
								? "rgba(234,179,8,0.08)"
								: "background.default",
							border: "1px solid",
							borderColor: currentEmployee
								? "rgba(234,179,8,0.3)"
								: "divider",
							borderRadius: 1,
						}}
					>
						<Typography
							variant="caption"
							fontWeight={700}
							color="text.secondary"
							sx={{
								textTransform: "uppercase",
								letterSpacing: "0.06em",
								mb: 0.5,
								display: "block",
							}}
						>
							Currently Assigned To
						</Typography>
						{currentEmployee ? (
							<Box>
								<Typography variant="body2" fontWeight={600}>
									{currentEmployee.label || "—"}
								</Typography>
								{currentAssignment?.assignedDate && (
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ mt: 0.5, display: "block" }}
									>
										Since{" "}
										{formatDate(
											currentAssignment.assignedDate,
										)}
									</Typography>
								)}
							</Box>
						) : (
							<Typography
								variant="caption"
								color="text.disabled"
								sx={{ fontStyle: "italic" }}
							>
								Unassigned
							</Typography>
						)}
					</Box>

					{/* New Assignment Dropdown */}
					<Box>
						<DropdownController
							name="perPersonnelId"
							label={
								isReassignment ? "Reassign To *" : "Assign To *"
							}
							options={availableEmployees}
							control={control}
							errors={errors}
							placeholder="— Select employee —"
						/>
						{selectedEmployee && (
							<Box
								sx={{
									mt: 1,
									p: 1,
									bgcolor: "rgba(99,102,241,0.06)",
									border: "1px solid rgba(99,102,241,0.2)",
									borderRadius: 1,
								}}
							>
								<Typography variant="caption" fontWeight={600}>
									{selectedEmployee.label}
								</Typography>
							</Box>
						)}
					</Box>

					{/* Reassignment Notice */}
					{isReassignment && currentEmployee && (
						<Box
							sx={{
								p: 1.5,
								bgcolor: "rgba(239,68,68,0.04)",
								border: "1px solid rgba(239,68,68,0.15)",
								borderRadius: 1,
							}}
						>
							<Typography variant="caption" color="error.main">
								⚠ This will end {currentEmployee.label}'s
								assignment and create a new assignment record.
							</Typography>
						</Box>
					)}

					{/* Notes */}
					<Box>
						<TextFieldController
							name="notes"
							label="Notes (optional)"
							control={control}
							errors={errors}
							multiline
							rows={2}
							placeholder="Reason for reassignment, shift change, etc..."
						/>
					</Box>
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				<PATSaveButton
					onClick={handleSubmit(onSubmit)}
					disabled={!perPersonnelId || loading}
					tooltipText={
						isReassignment ? "Save Reassignment" : "Save Assignment"
					}
					loading={loading}
				/>
				<PATCloseButton onClick={handleClose} tooltipText="Cancel" />
			</DialogActions>
		</Dialog>
	);
};

// ─── Assignment History Tab ──
const AssignmentHistoryTab = ({
	vehicle,
	assignmentHistory,
	classes,
	onAssignmentChange, // Callback to refresh data after assignment change
}) => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const userAgency = useSelector((state) => state.user.userAgency);
	const { userData } = useSelector((state) => state.user);

	const [employees, setEmployees] = useState([]);
	const [employeeOptions, setEmployeeOptions] = useState([]);
	const [showChangeAssign, setShowChangeAssign] = useState(false);
	const [loading, setLoading] = useState(false);

	const loadData = async () => {
		const emps = await getEmployees(userAgency, true); // true for options format
		const empList = await getEmployees(userAgency); // raw employee list
		setEmployeeOptions(emps);
		setEmployees(empList);
	};

	useEffect(() => {
		loadData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userAgency]);

	const getEmployeeInfo = (id) => {
		return employees?.find((e) => {
			const empId = typeof e.id === "string" ? decrypt(e.id) : e.id;
			// eslint-disable-next-line eqeqeq
			return empId == id;
		});
	};

	const vehAssignments = useMemo(() => {
		return (assignmentHistory || [])
			.filter((a) => a.vehicleId === vehicle?.id)
			.sort(
				(a, b) => new Date(b.assignedDate) - new Date(a.assignedDate),
			);
	}, [assignmentHistory, vehicle?.id]);

	const currentAssignment = useMemo(() => {
		return vehAssignments.find((a) => !a.unassignedDate);
	}, [vehAssignments]);

	const calculateDuration = (startDate, endDate) => {
		const start = new Date(startDate);
		const end = endDate ? new Date(endDate) : new Date();
		const days = Math.round((end - start) / (1000 * 60 * 60 * 24));
		return days;
	};

	// ── Handle Change Assignment ──
	const handleChangeAssignment = async ({ perPersonnelId, notes }) => {
		if (!perPersonnelId || !vehicle?.id) return;

		setLoading(true);

		try {
			const now = new Date().toISOString().split("T")[0]; // YYYY-MM-DD

			// 1. Close current assignment if exists
			if (currentAssignment) {
				await updateVehicleAssignmentHistory(currentAssignment.id, {
					unassignedDate: now,
					notes: currentAssignment.notes
						? `${currentAssignment.notes}\n[Reassigned] ${notes || ""}`
						: notes
							? `[Reassigned] ${notes}`
							: null,
				});
			}

			// 2. Create new assignment history record
			await createVehicleAssignmentHistory({
				vehicleId: vehicle.id,
				perPersonnelId: perPersonnelId,
				assignedDate: now,
				unassignedDate: null,
				assignedBy: userData?.user?.ptsUserID
					? String(userData.user.ptsUserID)
					: null,
				notes: notes || null,
				agencyId: userAgency,
				isDeleted: false,
			});

			// 3. Update vehicle's perPersonnelId
			await updateVehicleData(vehicle.id, {
				perPersonnelId: perPersonnelId,
			});

			dispatch(notify("Assignment changed successfully", "success"));
			setShowChangeAssign(false);

			// 4. Callback to refresh parent data
			if (onAssignmentChange) {
				onAssignmentChange();
			}
		} catch (error) {
			console.error("Failed to change assignment:", error);
			dispatch(notify("Failed to change assignment", "error"));
		} finally {
			setLoading(false);
		}
	};

	// ── Handle Remove Assignment (Check-In without reassigning) ──
	const handleRemoveAssignment = async () => {
		if (!currentAssignment || !vehicle?.id) return;

		setLoading(true);

		try {
			const now = new Date().toISOString().split("T")[0];

			// 1. Close current assignment
			await updateVehicleAssignmentHistory(currentAssignment.id, {
				unassignedDate: now,
			});

			// 2. Update vehicle to remove perPersonnelId
			await updateVehicleData(vehicle.id, {
				perPersonnelId: null,
			});

			dispatch(notify("Vehicle unassigned successfully", "success"));

			// 3. Callback to refresh parent data
			if (onAssignmentChange) {
				onAssignmentChange();
			}
		} catch (error) {
			console.error("Failed to remove assignment:", error);
			dispatch(notify("Failed to remove assignment", "error"));
		} finally {
			setLoading(false);
		}
	};

	return (
		<Box>
			{/* Header with Actions */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 2,
				}}
			>
				<Typography variant="body2" color="text.secondary">
					{currentAssignment
						? `Currently assigned · ${vehAssignments.length} total assignment${vehAssignments.length !== 1 ? "s" : ""}`
						: `Unassigned · ${vehAssignments.length} past assignment${vehAssignments.length !== 1 ? "s" : ""}`}
				</Typography>

				<Box sx={{ display: "flex", gap: 1 }}>
					{/* Remove Assignment Button (only if currently assigned) */}
					{currentAssignment && (
						<Button
							variant="outlined"
							color="warning"
							size="small"
							onClick={handleRemoveAssignment}
							disabled={loading}
							sx={{ textTransform: "none" }}
						>
							Unassign
						</Button>
					)}

					{/* Change/Assign Button */}
					<Button
						variant="contained"
						color="primary"
						size="small"
						startIcon={
							currentAssignment ? <SwapHoriz /> : <PersonAdd />
						}
						onClick={() => setShowChangeAssign(true)}
						disabled={loading || vehicle?.status === "Retired"}
						sx={{ textTransform: "none" }}
					>
						{currentAssignment
							? "Change Assignment"
							: "Assign Vehicle"}
					</Button>
				</Box>
			</Box>

			{/* Assignment History Table */}
			{vehAssignments.length === 0 ? (
				<Box className={classes.emptyState}>
					<Box className={classes.emptyIcon}>👤</Box>
					<Typography>
						No assignment history for this vehicle
					</Typography>
					<Button
						variant="outlined"
						size="small"
						startIcon={<PersonAdd />}
						onClick={() => setShowChangeAssign(true)}
						sx={{ mt: 2, textTransform: "none" }}
					>
						Assign Vehicle
					</Button>
				</Box>
			) : (
				<Box className={classes.tableWrap}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Employee</TableCell>
								<TableCell>Badge</TableCell>
								<TableCell>Division</TableCell>
								<TableCell>Assigned Date</TableCell>
								<TableCell>Unassigned Date</TableCell>
								<TableCell>Duration</TableCell>
								<TableCell>Assigned By</TableCell>
								<TableCell>Notes</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{vehAssignments.map((assignment) => {
								const employee = getEmployeeInfo(
									assignment.perPersonnelId,
								);
								const isCurrent = !assignment.unassignedDate;
								const duration = calculateDuration(
									assignment.assignedDate,
									assignment.unassignedDate,
								);

								return (
									<TableRow
										key={assignment.id}
										sx={{
											bgcolor: isCurrent
												? "rgba(34,197,94,0.04)"
												: "transparent",
										}}
									>
										<TableCell>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 1,
												}}
											>
												{isCurrent && (
													<Box
														sx={{
															width: 7,
															height: 7,
															borderRadius: "50%",
															bgcolor:
																theme.palette
																	.success
																	.main,
														}}
														title="Current"
													/>
												)}
												<Typography
													variant="body2"
													fontWeight={600}
												>
													{employee
														? getEmployeeOptionLabel(
																employee,
															)
														: "Unknown"}
												</Typography>
											</Box>
										</TableCell>
										<TableCell>
											<Typography
												variant="caption"
												sx={{ fontFamily: "monospace" }}
											>
												{employee?.badgeNum || "—"}
											</Typography>
										</TableCell>
										<TableCell>
											{employee?.department ||
												assignment.division ||
												"—"}
										</TableCell>
										<TableCell>
											<Typography
												variant="caption"
												sx={{ fontFamily: "monospace" }}
											>
												{formatDate(
													assignment.assignedDate,
												)}
											</Typography>
										</TableCell>
										<TableCell>
											{isCurrent ? (
												<Chip
													label="Current"
													size="small"
													color="success"
													sx={{
														height: 20,
														fontSize: 11,
														fontWeight: 600,
													}}
												/>
											) : (
												<Typography
													variant="caption"
													sx={{
														fontFamily: "monospace",
													}}
												>
													{formatDate(
														assignment.unassignedDate,
													)}
												</Typography>
											)}
										</TableCell>
										<TableCell>
											<Typography
												variant="caption"
												sx={{
													fontFamily: "monospace",
													color:
														duration > 365
															? theme.palette
																	.success
																	.main
															: "text.secondary",
												}}
											>
												{duration}d
											</Typography>
										</TableCell>
										<TableCell>
											<Typography
												variant="caption"
												color="text.secondary"
											>
												{assignment.assignedBy || "—"}
											</Typography>
										</TableCell>
										<TableCell sx={{ maxWidth: 200 }}>
											<Typography
												variant="caption"
												color="text.secondary"
												noWrap
											>
												{assignment.notes?.slice(0, 60)}
												{assignment.notes?.length > 60
													? "…"
													: ""}
											</Typography>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</Box>
			)}

			{/* Change Assignment Dialog */}
			<ChangeAssignmentDialog
				open={showChangeAssign}
				onClose={() => setShowChangeAssign(false)}
				onSave={handleChangeAssignment}
				vehicle={vehicle}
				employees={employeeOptions}
				currentAssignment={currentAssignment}
				loading={loading}
			/>
		</Box>
	);
};

export default AssignmentHistoryTab;
